import { ActivityEvent } from '../home/models/activity-event.model';

export interface WorkspaceActivityMeta {
  method: string;
  path: string;
  workspaceId?: string;
  libraryId?: string;
  id?: string;
}

export interface WorkspaceActivity {
  event: ActivityEvent;
  meta: WorkspaceActivityMeta;
}

type Rule = readonly [method: string, pattern: RegExp, event: ActivityEvent];

const SEG = '[^/]+';
const WS = `^/api/workspaces/(?<workspaceId>${SEG})`;
const LIB = `^/api/libraries/(?<libraryId>${SEG})`;
const ID = `(?<id>${SEG})`;

const rule = (method: string, pattern: string, event: ActivityEvent): Rule => [method, new RegExp(`${pattern}$`), event];

/** Gateway calls worth an activity record. Anything unlisted (list/read GETs, housekeeping POSTs) is ignored. */
const RULES: readonly Rule[] = [
  rule('POST', '^/api/workspaces', ActivityEvent.WorkspaceCreate),
  rule('PATCH', `^/api/workspaces/(?<workspaceId>${SEG})`, ActivityEvent.WorkspaceUpdate),
  rule('DELETE', `^/api/workspaces/(?<workspaceId>${SEG})`, ActivityEvent.WorkspaceDelete),

  rule('POST', `${WS}/smart-folders`, ActivityEvent.WorkspaceSmartFolderCreate),
  rule('PATCH', `${WS}/smart-folders/${ID}`, ActivityEvent.WorkspaceSmartFolderUpdate),
  rule('DELETE', `${WS}/smart-folders/${ID}`, ActivityEvent.WorkspaceSmartFolderDelete),

  rule('POST', `${WS}/charts`, ActivityEvent.WorkspaceChartCreate),
  rule('PATCH', `${WS}/charts/${ID}`, ActivityEvent.WorkspaceChartUpdate),
  rule('DELETE', `${WS}/charts/${ID}`, ActivityEvent.WorkspaceChartDelete),

  rule('POST', `${WS}/diagrams`, ActivityEvent.WorkspaceDiagramCreate),
  rule('PATCH', `${WS}/diagrams/${ID}`, ActivityEvent.WorkspaceDiagramUpdate),
  rule('DELETE', `${WS}/diagrams/${ID}`, ActivityEvent.WorkspaceDiagramDelete),

  rule('POST', `${WS}/sheets`, ActivityEvent.WorkspaceSheetCreate),
  rule('PATCH', `${WS}/sheets/${ID}`, ActivityEvent.WorkspaceSheetUpdate),
  rule('DELETE', `${WS}/sheets/${ID}`, ActivityEvent.WorkspaceSheetDelete),

  rule('PATCH', `${WS}/reports/${ID}`, ActivityEvent.WorkspaceReportUpdate),
  rule('DELETE', `${WS}/reports/${ID}`, ActivityEvent.WorkspaceReportDelete),
  rule('POST', `${WS}/reports/${ID}/save`, ActivityEvent.WorkspaceReportSave),
  rule('POST', `${WS}/reports/${ID}/run`, ActivityEvent.WorkspaceReportRun),

  rule('GET', `${WS}/search`, ActivityEvent.WorkspaceSearchQuery),
  rule('POST', `${WS}/db/query`, ActivityEvent.WorkspaceDatasetQuery),

  rule('POST', `${WS}/ai/agents`, ActivityEvent.WorkspaceAgentCreate),
  rule('PATCH', `${WS}/ai/agents/${ID}`, ActivityEvent.WorkspaceAgentUpdate),
  rule('DELETE', `${WS}/ai/agents/${ID}`, ActivityEvent.WorkspaceAgentDelete),
  rule('POST', `${WS}/ai/agents/${ID}/stop`, ActivityEvent.WorkspaceAgentStop),
  rule('POST', `${WS}/ai/agents/${ID}/interject`, ActivityEvent.WorkspaceAgentInterject),

  rule('POST', `${LIB}/documents`, ActivityEvent.WorkspaceDocumentUpload),
  rule('POST', `${LIB}/documents/finalize`, ActivityEvent.WorkspaceDocumentUpload),
  rule('GET', `${LIB}/documents/${ID}`, ActivityEvent.WorkspaceDocumentOpen),
  rule('GET', `${LIB}/documents/${ID}/similar`, ActivityEvent.WorkspaceDocumentSimilar),
  rule('GET', `${LIB}/documents/${ID}/compare/${SEG}`, ActivityEvent.WorkspaceDocumentCompare),
  rule('PATCH', `${LIB}/documents/${ID}`, ActivityEvent.WorkspaceDocumentUpdate),
  rule('PUT', `${LIB}/documents/${ID}/content`, ActivityEvent.WorkspaceDocumentUpdateContent),
  rule('POST', `${LIB}/documents/${ID}/reprocess`, ActivityEvent.WorkspaceDocumentReprocess),
  rule('POST', `${LIB}/documents/${ID}/steps/${SEG}/rerun`, ActivityEvent.WorkspaceDocumentReprocess),
  rule('POST', `${LIB}/documents/${ID}/stop`, ActivityEvent.WorkspaceDocumentStop),
  rule('DELETE', `${LIB}/documents/${ID}`, ActivityEvent.WorkspaceDocumentDelete),

  rule('POST', `${LIB}/folders`, ActivityEvent.WorkspaceFolderCreate),
  rule('PATCH', `${LIB}/folders/${ID}`, ActivityEvent.WorkspaceFolderUpdate),
  rule('DELETE', `${LIB}/folders/${ID}`, ActivityEvent.WorkspaceFolderDelete),

  rule('PUT', `^/api/db/datasets/${ID}`, ActivityEvent.WorkspaceDatasetUpload),
  rule('POST', `^/api/db/datasets/${ID}/finalize`, ActivityEvent.WorkspaceDatasetUpload),
  rule('PUT', `^/api/db/datasets/${ID}/schema`, ActivityEvent.WorkspaceDatasetUpdateSchema),
  rule('POST', `^/api/datasets/${ID}/verify`, ActivityEvent.WorkspaceDatasetVerify),
  rule('DELETE', `^/api/datasets/${ID}`, ActivityEvent.WorkspaceDatasetDelete),

  rule('POST', '^/api/grants', ActivityEvent.WorkspaceGrantCreate),
  rule('PATCH', `^/api/grants/${ID}`, ActivityEvent.WorkspaceGrantUpdate),
  rule('DELETE', `^/api/grants/${ID}`, ActivityEvent.WorkspaceGrantDelete),
];

/** Maps a workspace-gateway request to its activity event, or `undefined` when the call is not tracked. */
export function resolveWorkspaceActivity(method: string, url: string): WorkspaceActivity | undefined {
  const path = pathnameOf(url);
  if (!path?.startsWith('/api/')) {
    return undefined;
  }

  const upperMethod = method.toUpperCase();
  for (const [ruleMethod, pattern, event] of RULES) {
    if (ruleMethod !== upperMethod) {
      continue;
    }
    const match = pattern.exec(path);
    if (match) {
      return { event, meta: { method: upperMethod, path, ...decodedGroups(match.groups) } };
    }
  }

  return undefined;
}

function pathnameOf(url: string): string | undefined {
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return undefined;
  }
}

function decodedGroups(groups: Record<string, string> | undefined): Partial<WorkspaceActivityMeta> {
  return Object.fromEntries(Object.entries(groups ?? {}).map(([key, value]) => [key, safeDecode(value)]));
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}
